import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, CheckCircle2, XCircle, Tag as TagIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/AppLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

const SWATCHES = ["#f97316", "#0ea5e9", "#10b981", "#a855f7", "#ef4444", "#eab308", "#64748b"];

export default function Tags() {
  const [tags, setTags] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(SWATCHES[0]);
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tags");
      setTags(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/tags", { name: name.trim(), color, description });
      toast.success("Tag created");
      setName("");
      setDescription("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (t) => {
    try {
      await api.post(`/tags/${t.id}/${t.published ? "unpublish" : "publish"}`);
      toast.success(t.published ? "Unpublished" : "Published");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const del = (t) => {
    setConfirmState({
      title: "Delete this tag?",
      message: (
        <span>
          You're about to delete tag <strong>{t.name}</strong>. Questions already tagged keep the
          tag name on them but new reviewers will no longer see it as an option.
        </span>
      ),
      confirmLabel: "Delete tag",
      danger: true,
      onConfirm: async () => {
        try {
          await api.delete(`/tags/${t.id}`);
          toast.success("Tag deleted");
          load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title="Tags"
        subtitle="Create and publish tags reviewers can apply to questions."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form
          onSubmit={create}
          className="bg-white rounded-2xl border border-slate-100 brand-shadow p-6 h-fit"
        >
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>
            New Tag
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="tag-name-input"
                placeholder="e.g. High Yield"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              />
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                Color
              </label>
              <div className="flex gap-1.5">
                {SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-7 h-7 rounded-full border-2 ${
                      color === c ? "border-slate-900" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Optional"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="tag-create-btn"
              disabled={busy || !name.trim()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Create Tag
            </button>
          </div>
        </form>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 brand-shadow overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
              All Tags ({tags.length})
            </h3>
          </div>
          {tags.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <TagIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <div className="text-sm text-slate-400">No tags yet</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {tags.map((t) => (
                <div key={t.id} className="px-5 py-3.5 flex items-center gap-3">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: t.color || "#f97316" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm inline-flex items-center gap-2">
                      {t.name}
                      {t.system && (
                        <span
                          className="px-1.5 py-0.5 rounded-md text-[9px] font-mono uppercase tracking-wider bg-slate-900 text-white"
                          data-testid={`tag-system-${t.id}`}
                        >
                          System
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <div className="text-xs text-slate-500 truncate">{t.description}</div>
                    )}
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider ${
                      t.published
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {t.published ? "Published" : "Draft"}
                  </span>
                  {!t.system && (
                    <button
                      onClick={() => togglePublish(t)}
                      data-testid={`publish-tag-${t.id}`}
                      className={`p-1.5 rounded-md ${
                        t.published
                          ? "text-slate-500 hover:bg-slate-100"
                          : "text-emerald-600 hover:bg-emerald-50"
                      }`}
                      title={t.published ? "Unpublish" : "Publish"}
                    >
                      {t.published ? (
                        <XCircle className="w-4 h-4" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                  {!t.system && (
                    <button
                      onClick={() => del(t)}
                      data-testid={`delete-tag-${t.id}`}
                      className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {t.system && (
                    <span className="text-[10px] text-slate-400 font-mono italic px-2">
                      locked
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </PageContainer>
  );
}
